import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import { searchFormsRecyclingValidation } from '@ltvco/refresh-lib/utils';
import {
  SearchReportResponseData,
  usernameSearch,
  UsernameSearchParams,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  useForm,
  useMutation,
  yupResolver,
  useFeatureIsOn,
} from '@ltvco/refresh-lib/vendors';
import React, { useContext, useCallback } from 'react';

import { SearchButton } from '../components';
import { SearchProps } from '../utils';

import {
  SocialNetworkSelect,
  UsernameSearchFieldContainer,
  UsernameSearchDivider,
  UsernameFormContainer,
} from './components';
import { UsernameInput } from './UsernameInput';
import { socialNetworks, schemaUsernameSearchForm } from './utils';

export interface UsernameFormData {
  username: string;
  socialNetwork?: string;
}

export const UsernameSearch: React.FC<SearchProps> = ({
  isMobile,
  placeholderText,
  onSearchSuccess,
  placement = 'dashboard',
}) => {
  const {
    session: { account },
  } = useSession();
  const { logError, trackEventGA4 } = useContext(AppConfig);

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    control,
  } = useForm<UsernameFormData>({
    resolver: yupResolver(schemaUsernameSearchForm),
    defaultValues: {
      username: '',
      socialNetwork: '',
    },
  });

  const socialNetwork = watch('socialNetwork');
  const username = watch('username');
  const isUsernamePartialAddress = useFeatureIsOn('OAR-1706');
  const noResultsErrorMessage = 'No results found, please try again';

  const handleClearUsername = useCallback(() => {
    setValue('username', '');
    clearErrors('username');
  }, [setValue, clearErrors]);

  const usernameQuery = useMutation<
    SearchReportResponseData,
    Error,
    UsernameSearchParams
  >({
    mutationFn: usernameSearch,
    onSuccess: (data, variables) => {
      onSearchSuccess &&
        onSearchSuccess({
          permalink: data.report.permalink,
          reportType: 'username',
          searchParams: `username=${encodeURIComponent(variables.username)}`,
        });
    },
    onError: (error) => {
      logError('Username Search', error);
      setError('username', {
        message: noResultsErrorMessage,
      });
    },
  });

  const handleSocialNetworkChange = useCallback(
    (value: string) => {
      setValue('socialNetwork', value);
    },
    [setValue]
  );

  const onUsernameSearchSubmit = useCallback(
    ({ username, socialNetwork }: UsernameFormData) => {
      if (!searchFormsRecyclingValidation(account)) {
        return;
      }

      if (socialNetwork !== '') {
        username = `${username}@${socialNetwork}`;
      }

      trackEventGA4({
        event_name: 'report_search',
        report_type: 'username',
        search_by: 'username',
        search_from: `${placement}_search`,
      });

      usernameQuery.mutate({
        username,
        showPartialAddresses: isUsernamePartialAddress,
      });
    },
    [
      account,
      trackEventGA4,
      usernameQuery,
      clearErrors,
      isUsernamePartialAddress,
      placement,
    ]
  );

  const renderMobileForm = useCallback(() => {
    return (
      <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
        <UsernameSearchFieldContainer
          className={errors.username ? 'error' : ''}
        >
          <UsernameInput
            register={register}
            placeholder={placeholderText}
            errors={errors}
            clearErrors={(field?: string) => clearErrors(field as 'username')}
            onClear={handleClearUsername}
            value={username}
            control={control}
          />
        </UsernameSearchFieldContainer>

        <SocialNetworkSelect
          id="mobile-social-network-select"
          options={socialNetworks}
          value={socialNetwork}
          onOptionSelect={handleSocialNetworkChange}
          fullWidth
        />

        <SearchButton type="submit" fullWidth>
          Search
        </SearchButton>
      </Grid>
    );
  }, [
    register,
    placeholderText,
    socialNetwork,
    handleSocialNetworkChange,
    username,
    errors,
    clearErrors,
    handleClearUsername,
  ]);

  const renderDesktopForm = useCallback(() => {
    return (
      <UsernameFormContainer>
        <Box flexGrow={1}>
          <UsernameSearchFieldContainer
            container
            className={errors.username ? 'error' : ''}
          >
            <Grid
              size={{ xs: 7, lg: 9 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <UsernameInput
                register={register}
                placeholder={placeholderText}
                errors={errors}
                clearErrors={(field?: string) =>
                  clearErrors(field as 'username')
                }
                onClear={handleClearUsername}
                value={username}
                control={control}
              />
            </Grid>
            <Grid
              size={{ xs: 5, lg: 3 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <UsernameSearchDivider />

              <SocialNetworkSelect
                id="desktop-social-network-select"
                options={socialNetworks}
                value={socialNetwork}
                onOptionSelect={handleSocialNetworkChange}
              />
            </Grid>
          </UsernameSearchFieldContainer>
        </Box>
        <SearchButton type="submit">Search</SearchButton>
      </UsernameFormContainer>
    );
  }, [
    register,
    placeholderText,
    socialNetwork,
    handleSocialNetworkChange,
    username,
    errors,
    clearErrors,
    handleClearUsername,
  ]);

  return (
    <form onSubmit={handleSubmit(onUsernameSearchSubmit)}>
      {isMobile ? renderMobileForm() : renderDesktopForm()}
    </form>
  );
};
